import { useState, useEffect } from 'react'
import { Container, Row, Col, Card, Table, Form, Button, Spinner, Alert, Pagination, Badge, InputGroup } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import client from '../../api/client'

function UserList() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [searchEmail, setSearchEmail] = useState('')
  const [filterRole, setFilterRole] = useState('')
  const [filterActive, setFilterActive] = useState('')
  const perPage = 25

  useEffect(() => {
    fetchUsers()
  }, [page])

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (searchEmail) params.append('search', searchEmail)
      if (filterRole) params.append('role', filterRole)
      if (filterActive) params.append('active', filterActive)
      params.append('limit', perPage)
      params.append('offset', (page - 1) * perPage)

      const response = await client.get(`/admin/api/v1/users?${params}`)
      setUsers(response.data.users || response.data || [])
      setTotal(response.data.total || 0)
    } catch (err) {
      console.error('Error fetching users:', err)
      setError('Erreur lors du chargement des utilisateurs')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    setPage(1)
    fetchUsers()
  }

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'danger',
      influencer: 'warning',
      user: 'primary',
    }
    const labels = {
      admin: 'Admin',
      influencer: 'Créateur',
      user: 'Membre',
    }
    return <Badge bg={colors[role] || 'secondary'}>{labels[role] || role}</Badge>
  }

  const getStatusBadge = (active) => {
    if (active === false) {
      return <Badge bg="secondary">Inactif</Badge>
    }
    return <Badge bg="success">Actif</Badge>
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const totalPages = Math.ceil(total / perPage)

  return (
    <Container fluid className="py-3">
      <Row className="mb-3">
        <Col>
          <h1 className="h3">Gestion des utilisateurs</h1>
          <p className="text-muted">
            Consultez et gérez les comptes utilisateurs
          </p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Search and Filters */}
      <Card className="mb-4">
        <Card.Header>Recherche et filtres</Card.Header>
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Rechercher par email</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="email@exemple.com"
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Rôle</Form.Label>
                <Form.Select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                >
                  <option value="">Tous</option>
                  <option value="admin">Admin</option>
                  <option value="influencer">Créateur</option>
                  <option value="user">Membre</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Statut</Form.Label>
                <Form.Select
                  value={filterActive}
                  onChange={(e) => setFilterActive(e.target.value)}
                >
                  <option value="">Tous</option>
                  <option value="true">Actif</option>
                  <option value="false">Inactif</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Button variant="primary" onClick={handleSearch} className="w-100">
                Rechercher
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>Liste des utilisateurs</span>
          <span className="text-muted">{total} utilisateurs</span>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <p>Aucun utilisateur trouvé</p>
            </div>
          ) : (
            <>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Rôle</th>
                    <th>Statut</th>
                    <th>Abonnement</th>
                    <th>Créé le</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <strong>{user.email}</strong>
                        {user.name && <div className="small text-muted">{user.name}</div>}
                      </td>
                      <td>{getRoleBadge(user.role)}</td>
                      <td>{getStatusBadge(user.active)}</td>
                      <td>
                        {user.subscription_plan ? (
                          <Badge bg="info">{user.subscription_plan}</Badge>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>{formatDate(user.created_at)}</td>
                      <td>
                        <Link
                          to={`/users/${user.id}`}
                          className="btn btn-outline-primary btn-sm"
                        >
                          Détails
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <Pagination.First onClick={() => setPage(1)} disabled={page === 1} />
                    <Pagination.Prev onClick={() => setPage(page - 1)} disabled={page === 1} />
                    {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                      const pageNum = i + 1
                      return (
                        <Pagination.Item
                          key={pageNum}
                          active={page === pageNum}
                          onClick={() => setPage(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>
                      )
                    })}
                    {totalPages > 5 && <Pagination.Ellipsis />}
                    <Pagination.Next onClick={() => setPage(page + 1)} disabled={page === totalPages} />
                    <Pagination.Last onClick={() => setPage(totalPages)} disabled={page === totalPages} />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  )
}

export default UserList
